import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";

import { useOrganizationDetails } from "@hooks/queries/organization";
import { useApiRequest } from "@lib/hooks";
import { Role } from "@models/role";
import { useCurrentUser, useRole } from "@queries";
import { useStudents } from "@queries/organization-selectors";
import { useContainer } from "@src/contexts/container-context";
import { useAppStore } from "@src/stores/zustand/app-store";

import {
  exportOrganizationCeStats,
  getCareerRatings,
  getCeOrganizationDailyStats,
  getCeOrganizationStats,
  getCeOrganizationTopCareers,
  getStatsFileName,
} from "@services";
import { CeInsights, DashboardBanner } from "@views/dashboard/components";

import "./ce.tab.scss";

const getFilters = ({ classrooms, dateRange }) => {
  const filters = { dateRange };
  if (classrooms) {
    filters.classroomIds = classrooms.map((c) => c.classroomId);
  }

  return filters;
};

export const CeTab = () => {
  const { organizationService } = useContainer();

  const [topCareers, setTopCareers] = useState();
  const [orgStats, setOrgStats] = useState({ isLoading: true });
  const [dailyOrgStats, setOrgDailyStats] = useState({ isLoading: true });
  const [dateRange, setDateRange] = useState({});
  const [selectedClassrooms, setSelectedClassrooms] = useState();

  const currentUser = useCurrentUser();
  const role = useRole();
  const { organizationCode, userId } = currentUser || {};
  const students = useStudents({ role, orgCode: organizationCode, userId });
  const setDashboardLoaded = useAppStore((state) => state.setDashboardLoaded);
  const { orgCode = organizationCode } = useParams();
  const { data: organization } = useOrganizationDetails(orgCode);
  const explorersMap = new Map(students?.map((s) => [s.userId, s]));

  const { loading: downloadingFile, sendRequest: exportInsights } =
    useApiRequest(() => {
      const filters = getFilters({ classrooms: selectedClassrooms, dateRange });
      const fileName = getStatsFileName(organization?.name, dateRange);
      const code = role === Role.INSTRUCTOR.id ? userId : organization?.code;

      return exportOrganizationCeStats({
        organizationService,
        role,
        orgCodeOrUserId: code,
        filters,
        fileName,
        getMultiOrg: false,
      });
    }, false);

  useUnloadWarning({ trigger: downloadingFile });

  const getCeOrganizationSelectedStats = (orgCode, filters) => {
    Promise.all([
      getCeOrganizationDailyStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then((response) => setOrgDailyStats({ isLoading: false, response })),
      getCeOrganizationStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then((response) => setOrgStats({ isLoading: false, response })),
      getCeOrganizationTopCareers({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then(setTopCareers),
    ]).catch(() => {
      setOrgDailyStats({ isLoading: false });
      setOrgStats({ isLoading: false });
    });
  };

  const getExplorersRatings = async (career) => {
    const ratings = await getCareerRatings({
      organizationService,
      orgCode: organization?.code,
      userId,
      role,
      classroomIds: selectedClassrooms?.map((c) => c.classroomId) ?? undefined,
      learningExperienceId: career.learningExperienceId,
      dateRange,
    });

    return ratings.map((r) => {
      const { userId, rating } = r;
      const user = explorersMap.get(userId);
      return { ...user, rating };
    });
  };

  const onApplyFilters = ({ classrooms, dateRange }) => {
    const { startDate, endDate } = dateRange || {};
    clearOrgStats();
    setSelectedClassrooms(classrooms?.map((c) => c.data));
    setDateRange({ startDate, endDate });
  };

  const clearOrgStats = () => {
    setOrgDailyStats({ isLoading: true });
    setOrgStats({ isLoading: true });
  };

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    const filters = getFilters({ classrooms: selectedClassrooms, dateRange });
    if (startDate && endDate) {
      getCeOrganizationSelectedStats(organization?.code, filters);
    }
  }, [dateRange, selectedClassrooms]);

  useEffect(() => {
    if (!orgStats.isLoading && !dailyOrgStats.isLoading) {
      setDashboardLoaded();
    }
  }, [orgStats, dailyOrgStats]);

  const onExportStatsData = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      exportInsights();
    }
  };

  return (
    <div className="ce-single-tab">
      <DashboardBanner product={Product.CE} />
      <CeInsights
        userInfo={{ userId, role }}
        orgCode={organization?.code}
        classroomFilterEnabled
        generalStats={orgStats?.response}
        dailyStats={dailyOrgStats}
        onApplyFilters={onApplyFilters}
        topCareers={topCareers}
        onExportData={onExportStatsData}
        exportDataDisabled={downloadingFile}
        customExplorersFn={getExplorersRatings}
        classroom={selectedClassrooms}
      />
      <div className="notification-loader-container">
        {downloadingFile && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download of{" "}
            <strong>
              {selectedClassrooms?.length > 1
                ? `${selectedClassrooms.length} classrooms'`
                : selectedClassrooms?.length === 1
                ? `${selectedClassrooms[0].name}'s`
                : `${organization?.name}'s`}
            </strong>{" "}
            insights. Please wait...
          </Notification>
        )}
      </div>
    </div>
  );
};
